<template>
  <section
    v-if="products.length > 0"
    :class="bemBlock"
    :data-context="tileContext"
  >
    <p
      v-if="heading"
      :class="bemElement('heading')"
    >
      {{ heading }}
    </p>
    <div
      ref="swiper"
      :class="[bemElement('slider'), 'swiper', 'u-scrollbar-none']"
      itemtype="http://schema.org/SomeProducts"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(item, index) in products"
          :key="`product-slider-${index}`"
          class="swiper-slide"
          :class="bemElement('item')"
        >
          <BaseProductTile
            :context="tileContext"
            :keyid="`${item.product.id}-${index}`"
            :grid-index="index"
            :has-carousel="isBreakpointDesktop"
            :hide-tags="hideTileTags"
            :show-swatches="false"
            :product="item.product"
            :url="item.urls.product"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import getFrontConfig from '../../common/getFrontConfig';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseProductTile from './BaseProductTile.vue';
import getAnalyticsEventCenter from '../../common/getAnalyticsEventCenter';

const analyticsEventCenter = getAnalyticsEventCenter();

const {
  responsive: { bp_1025: bp1025 },
  vue: {
    BaseProductTile: {
      context,
    },
  },
} = getFrontConfig();

export default {
  name: 'BaseProductSlider',

  components: {
    BaseProductTile,
  },

  mixins: [
    bemClassesMixin,
    windowResizeMixin,
  ],

  props: {
    heading: {
      type: String,
      default: null,
    },
    products: {
      type: Array,
      default() {
        return [];
      },
    },
    tileContext: {
      type: String,
      default: context.pdpSlider,
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp1025,
    isSwiperImported: false,
    isSwiperInitialized: false,
    resizeObserver: null,
    swiperInstance: null,
  }),

  computed: {
    hideTileTags() {
      return this.tileContext === context.minicartSlider
        || this.tileContext === context.searchModal;
    },
  },

  watch: {
    products(newValue, oldValue) {
      if (newValue?.length !== oldValue?.length) {
        if (oldValue?.length > 0) {
          this.destroySwiper();
        }
        if (newValue?.length > 0) {
          this.initializeSwiper();
          this.sendAnalyticsEventProductSliderLoaded();
        }
        this.$eventBus.$emit('base-product-slider:updated', {
          productIds: this.products.map((item) => item.product.id),
        });
      }
    },
  },

  beforeCreate() {
    /*
    The component is prepared to load Swiper dinamically. However, it is is used
    whenever a carousel is shown in viewport. That condition is met when loading
    PLP, so will force to load the partial at the beginning.
    Although this partial seems useless, it will be cached by the browser and it
    will have less modifications than having code of the project.
    */
    import(/* webpackChunkName: "swiper.js" */ 'swiper').then(() => {
      this.isSwiperImported = true;
    });
  },

  beforeDestroy() {
    this.destroySwiper();
    this.resizeObserver?.disconnect();
  },

  mounted() {
    this.initializeSwiper();
    this.sendAnalyticsEventProductSliderLoaded();
  },

  methods: {
    destroySwiper() {
      this.swiperInstance?.destroy();
      this.isSwiperInitialized = false;
    },
    updateImageSliderHeight() {
      const imageSliderHeight = this.$refs.swiper.querySelector('.image-container')?.offsetHeight || 0;
      this.$refs.swiper.style.setProperty(
        '--image-slider-height',
        `${imageSliderHeight}px`,
      );
    },
    initializeSwiper() {
      if (this.isSwiperInitialized) return;
      if (this.products?.length <= 0) return;
      import(/* webpackChunkName: "swiper.js" */ 'swiper').then((module) => {
        const Swiper = module.default;
        this.swiperInstance = new Swiper(
          this.$refs.swiper,
          {
            loop: false,
            preventClicks: false,
            preventClicksPropagation: false,
            slidesPerView: 'auto',
            spaceBetween: 2,
            touchStartPreventDefault: false,
          },
        );
        this.isSwiperInitialized = true;
        this.$eventBus.$emit('base-product-slider:ready', {
          productIds: this.products.map((item) => item.product.id),
        });
        if (this.hideTileTags) {
          this.updateImageSliderHeight();
          if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(this.updateImageSliderHeight);
            this.resizeObserver.observe(this.$refs.swiper);
          }
        }
      });
    },
    sendAnalyticsEventProductSliderLoaded() {
      if (this.products.length > 0) {
        const currentContext = this.tileContext === context.searchModal
          ? context.search
          : this.tileContext;
        analyticsEventCenter.publish(
          analyticsEventCenter.EVENTS.productSliderLoaded,
          {
            context: currentContext,
            products: [...this.products],
          },
        );
      }
    },
  },
};
</script>
